import { DollarSign, AlertTriangle, Calendar } from "lucide-react";
import { Card, CardContent } from "../ui/card";

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
}

function formatCompact(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return formatCurrency(value);
}

function KPICard({ icon: Icon, label, value, color, subtext, compact = false }) {
  // A hairline card, never a tinted box — the status lives in the numeral and
  // the icon, not in a fill.
  const colorClasses = {
    default: "border-border text-foreground",
    green: "border-border text-green-700",
    orange: "border-border text-orange-700",
    red: "border-terracotta-200 text-terracotta-700",
    blue: "border-border text-estate-700"
  };
  
  const iconColors = {
    default: "text-muted-foreground",
    green: "text-green-600",
    orange: "text-orange-600",
    red: "text-red-600",
    blue: "text-blue-600"
  };

  const displayValue = typeof value === 'number' 
    ? (compact ? formatCompact(value) : formatCurrency(value)) 
    : value;

  return (
    <Card className={`${colorClasses[color || "default"]} min-w-0`}>
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
          <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${iconColors[color || "default"]} flex-shrink-0`} strokeWidth={1.5} />
          <p className="micro-label truncate">{label}</p>
        </div>
        <p className="text-xl sm:text-2xl xl:text-3xl tabular-nums truncate">
          {displayValue}
        </p>
        {subtext && <p className="text-[10px] sm:text-xs mt-0.5 sm:mt-1 opacity-60 truncate">{subtext}</p>}
      </CardContent>
    </Card>
  );
}

export default function MoneyKPIs({ summary }) {
  const outstandingThisMonth = summary.outstanding_this_month ?? (summary.expected_this_month - summary.collected_this_month);
  const nextMonthName = summary.next_month_name
    || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleString('en-US', { month: 'long' });

  return (
    <div className="mb-6 sm:mb-8">
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-4">
      <KPICard
        icon={AlertTriangle}
        label="Past Due"
        value={summary.past_due_total}
        color={summary.past_due_total > 0 ? "red" : "default"}
        subtext={summary.past_due_count > 0 ? `${summary.past_due_count} item(s)` : "All clear"}
        compact
      />
      <KPICard
        icon={DollarSign}
        label="Due This Month"
        value={outstandingThisMonth}
        color="blue"
        subtext={`${formatCompact(summary.collected_this_month)} collected`}
        compact
      />
      <KPICard
        icon={Calendar}
        label={`Due in ${nextMonthName}`}
        value={summary.next_month_total || 0}
        color="default"
        compact
      />
      <KPICard
        icon={Calendar}
        label="Next 3M"
        value={summary.next_3_months}
        color="default"
        compact
      />
      <KPICard
        icon={Calendar}
        label="Next 6M"
        value={summary.next_6_months}
        color="default"
        compact
      />
    </div>
    {summary.undated_total > 0 && (
      <p className="text-[11px] sm:text-xs text-amber-600 mt-2">
        {formatCompact(summary.undated_total)} across {summary.undated_count} receivable(s) with no due date — not included above. Set a due date to place them.
      </p>
    )}
    </div>
  );
}
